/**
 * SessionManager
 * 
 * セッション管理UI統合コンポーネント
 * 期限切れ・認証エラーの統合管理
 * @author @developer
 * @date 2025-09-05
 */

import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Alert, 
  Snackbar, 
  Typography,
  Box
} from '@mui/material';
import { 
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material';
import { useAuthContext } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * SessionManagerコンポーネント
 * 
 * 機能:
 * 1. セッション期限切れダイアログ表示
 * 2. 認証エラー通知（Snackbar）
 * 3. 自動ログアウト警告（将来対応）
 */
const SessionManager: React.FC = () => {
  const { error, clearError } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  
  // デバッグログ
  console.log('[SessionManager] currentPath:', currentPath);
  console.log('[SessionManager] error:', error);
  
  // ログイン・登録ページではSessionManagerのエラー表示を無効化
  // （各ページが独自のエラー表示を持つため）
  const isAuthPage = currentPath === '/login' || currentPath === '/register' || currentPath.startsWith('/email-verification');
  
  console.log('[SessionManager] isAuthPage:', isAuthPage);
  
  if (isAuthPage) {
    console.log('[SessionManager] Returning null for auth page');
    return null;
  }
  
  // セッション期限切れエラーの検出
  const isSessionExpired = Boolean(error && error.includes('セッションの更新に失敗'));
  
  // ログイン画面へのリダイレクト処理
  const handleLoginRedirect = () => {
    clearError();
    navigate('/login', { replace: true });
  };
  
  // 通常エラーのクリア処理
  const handleErrorClose = () => {
    clearError();
  };
  
  return (
    <>
      {/* セッション期限切れダイアログ */}
      <Dialog 
        open={isSessionExpired} 
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          pb: 1
        }}>
          <ErrorIcon color="error" />
          セッションが期限切れです
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            セキュリティのため、セッションの有効期限が切れました。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            再度ログインして続行してください。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleLoginRedirect} 
            variant="contained"
            startIcon={<LogoutIcon />}
            fullWidth
            sx={{ py: 1 }}
          >
            ログイン画面へ移動
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* その他の認証エラー通知（Snackbar） */}
      <Snackbar
        open={Boolean(error && !isSessionExpired)}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          top: { xs: 16, sm: 24 },
          zIndex: 9999
        }}
      >
        <Alert 
          severity="error" 
          onClose={handleErrorClose}
          variant="filled"
          sx={{
            minWidth: 300,
            alignItems: 'center'
          }}
          icon={<WarningIcon />}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              認証エラー
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {error}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
      
      {/* 自動ログアウト警告（将来実装） */}
      {/* 
      <Snackbar
        open={showAutoLogoutWarning}
        autoHideDuration={30000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9998 }}
      >
        <Alert 
          severity="warning"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={handleExtendSession}>
              セッション延長
            </Button>
          }
        >
          セッションが間もなく期限切れになります（30分前通知）
        </Alert>
      </Snackbar>
      */}
    </>
  );
};

export default SessionManager;